import React, { useState, useEffect } from 'react';
import { Users, Eye, Calendar, Hotel, X, Home, PlusCircle, RefreshCw } from 'lucide-react';
import { BusinessOffer, Package } from '../../../types/graphql';
import TravelersList from './TravelersList';
import OfferDetailsModal from './OfferDetailsModal';
import TravelersRoomPreferences from './TravelersRoomPreferences';
import AcceptedClientsRooms from './AcceptedClientsRooms';
import AcceptedClientsList from './AcceptedClientsList';
import AcceptedClientsRoomsList from './AcceptedClientsRoomsList';
import { useQuery } from '@apollo/client';
import { GET_OFFER_ACCEPTED_CLIENTS } from '../../../graphql/admin/queries/business';

interface OfferCardProps {
  offer: BusinessOffer;
  package: Package;
  onAddTravelers?: (offerId: number) => void;
  onRefresh?: () => void;
}

export default function OfferCard({ offer, package: pkg, onAddTravelers, onRefresh }: OfferCardProps) {
  const [showTravelers, setShowTravelers] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showClientRooms, setShowClientRooms] = useState(false);
  const [showAcceptedClients, setShowAcceptedClients] = useState(false);

  const hasBookings = offer.bookings && offer.bookings.length > 0;
  const totalTravelers = hasBookings ? offer.bookings[0].travelerInfo.length : 0;
  
  // Récupérer les clients acceptés
  const { data: clientsData, loading, error } = useQuery(GET_OFFER_ACCEPTED_CLIENTS, {
    variables: { offerId: offer.id },
    skip: !showAcceptedClients,
    fetchPolicy: 'network-only' // Forcer une requête réseau à chaque fois
  });
  
  // Vérifier si l'offre a des clients acceptés
  const hasAcceptedClients = offer.acceptedClients && offer.acceptedClients.length > 0;

 

  

  const getStatusColor = (status: BusinessOffer['status']) => {
    switch (status) {
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status: BusinessOffer['status']) => {
    switch (status) {
      case 'ACCEPTED':
        return 'Acceptée';
      case 'REJECTED':
        return 'Refusée';
      default:
        return 'En attente';
    }
  };

  // Créer un objet offre complet avec le package
  const completeOffer = {
    ...offer,
    package: pkg
  };

  const handleAddTravelers = () => {
    if (onAddTravelers) {
      onAddTravelers(offer.id);
    }
  };

  // Modifions la fonction getImageUrl pour utiliser import.meta.env
  const getImageUrl = (imagePath: string | undefined) => {
    if (!imagePath) {
      return null;
    }
    const fullUrl = imagePath?.startsWith('http') 
      ? imagePath 
      : `${import.meta.env.VITE_API_URL || ''}${imagePath}`;
    return fullUrl;
  };

  return (
    <div className="p-6">
      <div className="relative w-full h-48 mb-4 rounded-lg overflow-hidden">
        {pkg?.image ? (
          <>
            <img 
              src={getImageUrl(pkg.image)} 
              alt={pkg.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error("Erreur de chargement de l'image:", e);
                e.currentTarget.src = "https://via.placeholder.com/400x200?text=Image+non+disponible";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </>
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500">Aucune image disponible</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-0 left-0 p-4">
          <h3 className="text-lg font-semibold text-white">{offer.title}</h3>
          <p className="text-sm text-white/90">ID: {offer.id}</p>
        </div>
        <span className={`absolute top-4 right-4 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(offer.status)}`}>
          {getStatusText(offer.status)}
        </span>
      </div>

      <div className="mt-4">
        <p className="text-gray-600">{offer.description}</p>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div>
            <p>Package: {pkg.title}</p>
            <p>Réduction: {offer.discount}%</p>
          </div>
          <div>
            <p>Valable jusqu'au: {new Date(offer.validUntil).toLocaleDateString('fr-FR')}</p>
            <p>Min. participants: {offer.minimumParticipants}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t">
        <div className="flex justify-between items-center">
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowDetails(true)}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Voir les détails
            </button>
            
            {hasBookings ? (
              <>
                <button
                  onClick={() => setShowTravelers(true)}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  Voir les voyageurs
                </button>
                {offer.status === 'ACCEPTED' && (
                  <button
                    onClick={() => setShowClientRooms(true)}
                    className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 border border-green-600 rounded-lg hover:bg-green-50 transition-colors"
                  >
                    Voir les chambres
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handleAddTravelers}
                className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 border border-green-600 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-2"
              >
                <PlusCircle className="w-4 h-4" />
                Ajouter des voyageurs
              </button>
            )}
            
            {offer.status === 'ACCEPTED' && (
              <button
                onClick={() => setShowAcceptedClients(true)}
                className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 border border-green-600 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Voir les clients acceptés
                {hasAcceptedClients && <span className="ml-1">({offer.acceptedClients.length})</span>}
              </button>
            )}
          </div>
        </div>
      </div>

      {showTravelers && hasBookings && (
        <TravelersList
          booking={offer.bookings[0]}
          onClose={() => setShowTravelers(false)}
        />
      )}

      {showDetails && (
        <OfferDetailsModal
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          offerId={offer.id}
          offer={completeOffer}
        />
      )}

      {showClientRooms && hasBookings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Voyageurs et préférences de chambres</h3>
              <button
                onClick={() => setShowClientRooms(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {offer.bookings.map((booking, index) => (
              <div key={`booking-${index}`} className="mb-6">
                <h4 className="font-medium mb-3">Réservation #{booking.id}</h4>
                <TravelersRoomPreferences
                  travelers={booking.travelerInfo}
                  roomPreferences={booking.roomPreferences}
                />
              </div>
            ))}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowClientRooms(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {showAcceptedClients && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Clients acceptés</h3>
              <button
                onClick={() => setShowAcceptedClients(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {clientsData?.businessOffer?.rooms?.length > 0 ? (
              <AcceptedClientsRoomsList rooms={clientsData.businessOffer.rooms} />
            ) : (
              <p className="text-gray-600">Aucun client accepté pour cette offre.</p>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAcceptedClients(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}